import {
  Autocomplete,
  AutocompleteFreeSoloValueMapping,
  AutocompleteValue,
  Box,
} from '@mui/material'
import { useRef, useState } from 'react'
import { RxChevronDown } from 'react-icons/rx'
import { tss } from 'tss-react/mui'
import {
  RecordCellEditOrigin,
  useEditableTableCellOriginContext,
} from '~/components/table-cells/useEditableTableCellOriginProvider'
import { Chip } from '../../atoms/chip/Chip'
import { MenuItem } from '../../atoms/menu-item/MenuItem'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { TextField, TextFieldProps } from '../text-field/TextField'
import { DropdownProps, DropdownVariant } from './types'
import {
  dropdownChipPrimitives,
  dropdownMenuItemPrimitives,
  dropdownMenuPrimitives,
  getAutocompleteBasePrimitives,
  getDropdownBaseInputPrimitives,
} from './variants/primitives'

/**
 * Renders an autocomplete component with text input and dropdown menu
 */
export function Dropdown<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  variant = 'default',
  getOptionLabel,
  getOptionDisplay,
  error,
  helperText,
  className,
  disabled,
  onChange,
  label,
  multiple = false as Multiple,
  freeSolo = false as FreeSolo,
  disableClearable = true as DisableClearable,
  value,
  showCheckmark = true,
  getOptionDisabled,
  getDisabledReason,
  tooltipPlacement,
  clearIcon,
  rounded = false,
  placeholder,
  isBorderless = false,
  rows,
  ...rest
}: DropdownProps<T, Multiple, DisableClearable, FreeSolo> &
  Pick<TextFieldProps, 'error' | 'helperText' | 'rows'>) {
  const readOnly = !multiple && !!value

  const [inputValue, setInputValue] = useState('')
  const origin = useEditableTableCellOriginContext()

  const dropdownRef = useRef<HTMLInputElement>(null)

  const { cx, classes } = useStyles({ readOnly, variant, rounded, isBorderless })

  const isSelected = (option: T) => {
    if (Array.isArray(value)) {
      return value.includes(option)
    } else {
      return value === option
    }
  }

  const isInEditableTableCellForm = origin === RecordCellEditOrigin.TABLE_DETAILS

  return (
    <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
      ref={dropdownRef}
      className={cx(classes.autocomplete, className)}
      clearIcon={<></>}
      disableClearable={disableClearable}
      disableCloseOnSelect={multiple && !isInEditableTableCellForm}
      disabled={disabled}
      freeSolo={freeSolo}
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={(option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) => {
        if (typeof option === 'string') {
          return option // Handle the string for freeSolo.
        }
        // Now TypeScript knows option is of type T.
        return getOptionLabel(option)
      }}
      inputValue={inputValue} // Controlled input value
      multiple={multiple}
      onChange={(event, value, reason) => {
        if (onChange) {
          onChange(event, value, reason)
        }
        if (reason === 'selectOption' || reason === 'createOption') {
          // When user selects or creates an option, clear the text
          setInputValue('')
        }
      }}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input' && readOnly) {
          // If read only, typing into input should not update the input value
          event.stopPropagation()
        } else if (reason === 'reset') {
          // Remove the text when the user makes a selection
          setInputValue('')
        } else {
          setInputValue(newInputValue)
        }
      }}
      onKeyDownCapture={(event) => {
        if (event.key === 'Enter') {
          if ((event.ctrlKey || event.metaKey) && inputValue === '' && multiple) {
            event.preventDefault()
            event.stopPropagation()
            event.currentTarget.closest('form')?.requestSubmit()
          }
        }

        // Certain event handlers block the use of "s" because they think it is a shortcut being
        // used for save. This overwrite forces the letter "s" to be usable.
        if (event.key.toLowerCase() === 's') {
          event.preventDefault()
          event.stopPropagation()
          setInputValue((value) => value + event.key)
        }
      }}
      openOnFocus
      popupIcon={<RxChevronDown size="16px" />}
      renderInput={(params) => {
        // Open state and error message visibility
        const isOpen = params.inputProps['aria-expanded']
        const showErrorMessage = !isOpen && !disabled

        // Determine the display value for the selected option
        const displayValue =
          !Array.isArray(value) && value && !multiple ? getOptionLabel(value as T) : inputValue

        // RenderTags only works on multiple select so for single select, need to pass in a chip
        // for the start adornment
        let startAdornment = rest.startAdornment
        if (!multiple && variant === 'chip' && value) {
          startAdornment = (
            <Chip
              className={classes.chip}
              color="gray"
              disabled={disabled}
              isRounded={false}
              label={displayValue}
              // Do not allow for deletion of selection option if the component is disabled
              onDelete={
                disabled
                  ? undefined
                  : (event) => {
                      if (onChange) {
                        const newValue = null as unknown as AutocompleteValue<
                          T,
                          Multiple,
                          DisableClearable,
                          FreeSolo
                        >
                        onChange(event, newValue, 'clear')
                      }
                    }
              }
              variant="solid"
            />
          )
        }

        // Extract InputProps and InputLabelProps from Autocomplete params
        // This is necessary to avoid passing invalid props to the native input element,
        // which would throw the "React does not recognize the `InputProps` prop on a DOM element." error.
        const { InputProps, InputLabelProps, ...restParams } = params

        // Modify html native input props based on the selection type and variant
        const modifiedInputProps = {
          ...params.inputProps,
          // Clear the text field value when a chip is used for display
          value: !multiple && variant === 'chip' && value ? '' : displayValue,
        }
        return (
          <Box ref={InputProps.ref}>
            <TextField
              autoFocus={isInEditableTableCellForm || rest.autoFocus}
              {...restParams}
              className={classes.input}
              endAdornment={
                <div className="flex items-center gap-1">
                  {value && clearIcon}
                  <div className={classes.endAdornment}>{InputProps.endAdornment}</div>
                </div>
              }
              error={error}
              helperText={showErrorMessage ? helperText : undefined}
              inputProps={modifiedInputProps}
              label={label}
              multiline={multiple}
              placeholder={placeholder}
              rows={rows}
              startAdornment={startAdornment ?? InputProps.startAdornment}
              variant={rounded ? 'rounded' : undefined}
            />
          </Box>
        )
      }}
      renderOption={(props, option: T) => {
        const menuItemDisplay = getOptionDisplay ? getOptionDisplay(option) : getOptionLabel(option)
        const isDisabled = getOptionDisabled ? getOptionDisabled(option) : false
        const disabledReason = isDisabled && getDisabledReason ? getDisabledReason(option) : ''

        return (
          <Tooltip key={props.id} placement={tooltipPlacement} title={disabledReason}>
            <div>
              <MenuItem
                disabled={isDisabled}
                label={menuItemDisplay}
                selected={isSelected(option)}
                showCheckmark={showCheckmark}
                variant={variant}
                {...props}
                className={classes.menuItem}
              >
                {getOptionDisplay && getOptionDisplay(option)}
              </MenuItem>
            </div>
          </Tooltip>
        )
      }}
      renderTags={(value, getTagProps) => {
        return value.map((option, index) => {
          const tagProps = getTagProps({ index })
          return (
            <Chip
              color={'gray'}
              isRounded={false}
              label={getOptionLabel(option)}
              variant={'solid'}
              {...tagProps}
              key={index}
              className={classes.chip}
              disabled={disabled}
              // Do not show the remove icon on chip if the entire dropdown is disabled
              onDelete={disabled ? undefined : tagProps.onDelete}
            />
          )
        })
      }}
      slotProps={{
        popupIndicator: {
          disableFocusRipple: true,
          disableTouchRipple: true,
        },
        popper: {
          className: classes.menuContainer,
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
          ],
        },
      }}
      value={value}
      {...rest}
    />
  )
}

const useStyles = tss
  .withParams<{
    readOnly: boolean
    variant: DropdownVariant
    rounded: boolean
    isBorderless?: boolean
  }>()
  .create(({ readOnly, variant, rounded, isBorderless }) => {
    return {
      autocomplete: getAutocompleteBasePrimitives(variant, isBorderless),
      input: getDropdownBaseInputPrimitives(readOnly, rounded),
      menuContainer: dropdownMenuPrimitives,
      chip: dropdownChipPrimitives,
      menuItem: dropdownMenuItemPrimitives,
      endAdornment: {
        '& .MuiAutocomplete-endAdornment': {
          top: undefined,
        },
      },
    }
  })
